import { useSpring } from '@react-spring/web';
import { useCallback, useEffect, useRef } from 'react';
import {
  clamp,
  getLengthOfLine,
  getMiddleTouchOnElement,
  isTouchesInsideRect,
} from './helpers';

type OnScale = (scaled: { scale: number }) => void;

export interface ZoomHookOptions {
  minScale?: number;
  maxScale?: number;
  onScale?: OnScale;
}

export default function useZoom({
  minScale,
  maxScale,
  onScale,
}: ZoomHookOptions): any {
  const element = useRef<HTMLDivElement>(null);
  const initialBoundingRect = useRef<DOMRect>(null);
  const firstTouch = useRef<[x: number, y: number]>(null);
  const initialPinchLength = useRef<number>(null);

  const [{ scale, middleTouchOnElement, translateX, translateY }, set] =
    useSpring(() => ({
      scale: 1,
      middleTouchOnElement: [0, 0],
      translateX: 0,
      translateY: 0,
      immediate: true,
      onFrame: ({ scale: currentScale }) => {
        if (typeof onScale === 'function') {
          onScale({ scale: currentScale });
        }
      },
    }));

  const handleTouchStart = useCallback(
    event => {
      if (event.touches.length !== 2) {
        return;
      }

      // @ts-ignore
      initialBoundingRect.current = element.current?.getBoundingClientRect?.();

      if (
        !event.touches.length ||
        !isTouchesInsideRect(event.touches, initialBoundingRect.current)
      ) {
        return;
      }

      event.preventDefault();

      const [touch1, touch2] = event.touches;
      const { clientX, clientY } = getMiddleTouchOnElement(
        event.touches,
        initialBoundingRect.current,
      );

      // @ts-ignore
      firstTouch.current = [clientX, clientY];
      // @ts-ignore
      initialPinchLength.current = getLengthOfLine(touch1, touch2);

      set({ middleTouchOnElement: [clientX, clientY], immediate: true });
    },
    [set],
  );

  const handleTouchMove = useCallback(
    event => {
      if (firstTouch.current != null && initialPinchLength.current != null) {
        const currentMiddleTouchOnElement = getMiddleTouchOnElement(
          event.touches,
          initialBoundingRect.current,
        );

        const [touch1, touch2] = event.touches;
        const currentPinchLength = getLengthOfLine(touch1, touch2);

        set({
          scale: clamp(
            currentPinchLength / initialPinchLength.current,
            minScale,
            maxScale,
          ),
          translateX:
            currentMiddleTouchOnElement.clientX - firstTouch.current[0],
          translateY:
            currentMiddleTouchOnElement.clientY - firstTouch.current[1],
          immediate: true,
        });
      }
    },
    [set],
  );

  const handleTouchEnd = useCallback(() => {
    set({
      scale: 1,
      translateX: 0,
      translateY: 0,
      immediate: false,
    });

    // @ts-ignore
    firstTouch.current = null;
    // @ts-ignore
    initialPinchLength.current = null;
    // @ts-ignore
    initialBoundingRect.current = null;
  }, [set]);

  useEffect(() => {
    // @ts-ignore
    element.current.ontouchstart = handleTouchStart;
    // @ts-ignore
    element.current.ontouchmove = handleTouchMove;
    // @ts-ignore
    element.current.ontouchend = handleTouchEnd;
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return [
    element,
    scale,
    translateX,
    translateY,
    middleTouchOnElement,
  ] as const;
}
